import { format } from 'date-fns';
import { Bot } from 'lucide-react';
import pluralize from 'pluralize';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { cn } from '@/lib/shadcn/utils';
import { useCheckPermissions } from '@/modules/auth/hooks/useCheckPermissions';
import { permissions } from '@/modules/auth/permissions';
import { useWorkspacePlan } from '@/modules/billing/hooks/useWorkspacePlan';
import { FindMoreCandidates } from '@/modules/hiring-manager/components/findMoreCandidates/FindMoreCandidates';
import { JobStatusMenu } from '@/modules/hiring-manager/components/jobStatusMenu/JobStatusMenu';
import {
  canUserFindMoreCandidates,
  canUserRetryFailedSourcing,
} from '@/modules/hiring-manager/utils/jobSearchStatus';
import { useRetryJobSourcingMutation } from '@/modules/jobs/mutations/useRetryJobSourcingMutation';
import type { JobApi } from '@/modules/jobs/types';
import { Button } from '@/modules/ui/components/button/Button';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

export type JobPageTab = 'candidates' | 'automation';

interface JobPageHeaderProps {
  activeTab: JobPageTab;
  /** Shown between the title and the tabs, e.g. the job's Finlay section. */
  children?: ReactNode;
  /** Shown next to the Candidates tab once the list has loaded. */
  candidateCount?: number;
  job: JobApi;
  searchSuffix: string;
}

/**
 * One header for the job's pages: title, meta, state, and the
 * Candidates | Automation tabs, so the two are tabs of the same thing.
 */
export function JobPageHeader({
  activeTab,
  children,
  candidateCount,
  job,
  searchSuffix,
}: JobPageHeaderProps) {
  const { plan, isLoading, error } = useWorkspacePlan();
  const hasUserPermission = useCheckPermissions();
  const retrySourcing = useRetryJobSourcingMutation();
  const canEditJob = !isLoading && !error && plan !== 'managed';
  const isPaused = job.state === 'OnHold';
  const isClosed = job.state === 'Hired' || job.state === 'Closed';
  // Drafts and unpublished-but-searching jobs are not live yet, so they have
  // nothing to pause or close.
  const canChangeState =
    (job.state === 'Published' || isPaused) &&
    hasUserPermission([permissions.Jobs.Update]);
  const isSourcingFailed = job.sourcingState === 'Failed';
  const isSourcingComplete = job.sourcingState === 'Finished';
  const canRetrySourcing = canUserRetryFailedSourcing(job, {
    isSelfService: canEditJob,
    hasPermission: hasUserPermission([permissions.Jobs.StartSourcing]),
  });
  const canFindMore = canUserFindMoreCandidates(job, {
    isSelfService: canEditJob,
    hasPermission:
      hasUserPermission([permissions.Jobs.StartSourcing]) &&
      (hasUserPermission([permissions.Jobs.Update]) ||
        hasUserPermission([permissions.Jobs.ContinueSourcing])),
  });
  const meta = [
    job.location,
    `Sourcing since ${format(new Date(job.createdAt), 'MMM d')}`,
    job.candidateCount != null &&
      `${job.candidateCount} ${pluralize('candidate', job.candidateCount)} so far`,
  ].filter(Boolean);

  const tabs: { key: JobPageTab; label: string; to: string }[] = [
    {
      key: 'candidates',
      label: 'Candidates',
      to: `/hm/jobs/${job.id}/candidates${searchSuffix}`,
    },
    {
      key: 'automation',
      label: 'Automation',
      to: `/hm/jobs/${job.id}/automation${searchSuffix}`,
    },
  ];

  // The state badge flipping back to Sourcing is the confirmation.
  const handleRetrySourcing = () => {
    retrySourcing.mutate(
      { jobId: job.id },
      {
        onError: (retryError) =>
          toast.error(actionErrorMessage('restart sourcing', retryError)),
      },
    );
  };

  return (
    <div className="flex flex-col gap-5">
      {/* From `sm` the state and actions keep their corner and the meta
          wraps instead, so opening the candidate panel does not drop them
          under the title. */}
      <div className="flex flex-wrap items-end justify-between gap-4 sm:flex-nowrap">
        <div className="min-w-0">
          <h1 className="truncate text-[22px] leading-7 font-semibold tracking-[-0.01em]">
            {job.title}
          </h1>
          <p className="text-muted-foreground mt-1 text-[13px]">
            {meta.join(' · ')}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-4">
          {isClosed ? (
            <span className="inline-flex h-5 items-center gap-1.5 text-xs font-medium text-neutral-500">
              <span className="size-1.5 rounded-full bg-neutral-500" />
              {job.state === 'Hired' ? 'Closed · filled' : 'Closed'}
            </span>
          ) : isPaused ? (
            <span className="inline-flex h-5 items-center gap-1.5 text-xs font-medium text-neutral-500">
              <span className="size-1.5 rounded-full bg-neutral-500" />
              Paused
            </span>
          ) : isSourcingFailed ? (
            <span className="inline-flex h-5 items-center gap-1.5 text-xs font-medium text-amber-700">
              <span className="size-1.5 rounded-full bg-amber-500" />
              Sourcing failed
            </span>
          ) : isSourcingComplete ? (
            <span className="inline-flex h-5 items-center gap-1.5 text-xs font-medium text-neutral-500">
              <span className="size-1.5 rounded-full bg-neutral-500" />
              Sourcing complete
            </span>
          ) : (
            <span className="text-muted-foreground inline-flex h-5 items-center gap-1.5 text-xs font-medium">
              <span className="bg-semantic-560 size-1.5 animate-pulse rounded-full motion-reduce:animate-none" />
              Sourcing
            </span>
          )}
          {canRetrySourcing && (
            <Button
              className="h-8 rounded-md"
              loading={retrySourcing.isPending}
              onClick={handleRetrySourcing}
              size="sm"
              variant="warning"
            >
              Retry sourcing
            </Button>
          )}
          {canFindMore && (
            <FindMoreCandidates
              billingHref={`/hm/billing${searchSuffix}`}
              job={job}
            />
          )}
          {canEditJob &&
            !isClosed &&
            (canChangeState ? (
              <JobStatusMenu job={job} searchSuffix={searchSuffix} />
            ) : (
              <Button
                asChild
                className="h-8 rounded-md"
                size="sm"
                variant="outline"
              >
                <Link to={`/hm/jobs/${job.id}/edit${searchSuffix}`}>
                  Edit job
                </Link>
              </Button>
            ))}
        </div>
      </div>

      {children}

      <nav aria-label="Job pages" className="flex gap-6 border-b">
        {tabs.map((tab) => {
          if (tab.key === 'automation' && !canEditJob) return null;
          const isActive = tab.key === activeTab;
          return (
            <Link
              aria-current={isActive ? 'page' : undefined}
              className={cn(
                '-mb-px inline-flex items-center gap-1.5 border-b pb-2.5 text-[13px] font-medium transition-colors',
                isActive
                  ? 'border-foreground text-foreground'
                  : 'text-muted-foreground hover:text-foreground border-transparent',
              )}
              key={tab.key}
              to={tab.to}
            >
              {/* Marks the tab where the user decides what Finlay handles. */}
              {tab.key === 'automation' && (
                <Bot aria-hidden className="text-primary size-3.5" />
              )}
              {tab.label}
              {tab.key === 'candidates' && candidateCount !== undefined && (
                <span className="text-neutral-500 tabular-nums">
                  {candidateCount}
                </span>
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
